/**
 * References — service layer (docs/REFERENCES.md). One packet per Opportunity, picking an ordered
 * subset of the firm-wide Reference Library (services/referenceLibrary.js) for a letter-format
 * PDF, released to the Client Hub independently of proposal status. Backed by api/references.js
 * (Supabase `crm_references`).
 */

async function clerkToken() {
  try { return typeof window.__clerkGetToken === 'function' ? await window.__clerkGetToken().catch(() => null) : null } catch { return null }
}
async function authHeaders() {
  const token = await clerkToken()
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) }
}
async function call(body) {
  const res = await fetch('/api/references', { method: 'POST', headers: await authHeaders(), body: JSON.stringify(body) })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(data.error || ('Reference request failed (' + res.status + ')'))
  return data
}

/** References for one opportunity, newest first. */
export async function getReferencesForOpportunity(opportunityId) {
  const headers = await authHeaders()
  const res = await fetch('/api/references?opportunityId=' + encodeURIComponent(opportunityId), { headers })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(data.error || ('Reference list failed (' + res.status + ')'))
  return data.references
}

export function createReference(opportunityId, { title, intro, referenceIds } = {}) {
  return call({ action: 'create', opportunityId, title, intro, referenceIds }).then(d => d.reference)
}
export function updateReference(id, { title, intro, referenceIds } = {}) {
  return call({ action: 'update', id, title, intro, referenceIds }).then(d => d.reference)
}
export function deleteReference(id) {
  return call({ action: 'delete', id })
}

/** Generate the reference-letter PDF. working:true = preview (WORKING DRAFT stamp). Returns a blob object URL. */
export async function previewReferencePdf(id, { working = true } = {}) {
  const res = await fetch('/api/references', { method: 'POST', headers: await authHeaders(), body: JSON.stringify({ action: 'preview', id, working }) })
  if (!res.ok) { let e; try { e = (await res.json()).error } catch (_) {} throw new Error(e || 'PDF generation failed') }
  return URL.createObjectURL(await res.blob())
}

/** Issue + store the final PDF (sets pdf_url) — does NOT mark posted. Returns { url }. */
export function freezeReferencePdf(id) {
  return call({ action: 'freeze', id })
}

/** Mark a reference letter posted after its journey entry exists — pdfUrl/journeyEntryId tie the two together. */
export function markReferencePosted(id, { pdfUrl, journeyEntryId } = {}) {
  return call({ action: 'markPosted', id, pdfUrl, journeyEntryId }).then(d => d.reference)
}

/** TEST/ADMIN ONLY (/debug mode) — reverts posted state so the flow can be re-run. */
export function unpostReference(id) {
  return call({ action: 'unpost', id }).then(d => d.reference)
}
